import { isAdministratorRole, normalizeAdminRole } from '../../../utils/ecmwfExcelImport';

export const CG_STATUSES = ['created', 'send', 'paid', 'cancelled'];
export const CG_EDITABLE_STATUSES = ['created', 'paid', 'cancelled'];
export const CG_SENDABLE_STATUSES = ['created'];

export const CG_STATUS_CONFIG = {
    created: { label: 'Créé', bg: 'primary' },
    send: { label: 'Lien RIB envoyé', bg: 'warning' },
    paid: { label: 'Payé', bg: 'success' },
    cancelled: { label: 'Annulé', bg: 'danger' },
};

export const canSendCgRibLink = (status) => CG_SENDABLE_STATUSES.includes(status);

export const CG_PER_PAGE_OPTIONS = [10, 20, 50, 100];
export const CG_DEFAULT_PER_PAGE = 20;

export const getCommercialGesturePermissions = (role) => {
    const normalized = normalizeAdminRole(role);
    const canWrite = isAdministratorRole(normalized) || normalized === 'restricted_admin';
    const canRead = canWrite || normalized === 'readonly_reporting';
    return { canRead, canWrite };
};

const amountFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });

export const formatCgAmount = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const number = Number(value);
    return Number.isFinite(number) ? amountFormatter.format(number) : String(value);
};

const parisDateFormatter = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});

/** "2026-09-28T10:00:00Z" → "28/09/2026 12:00" (heure de Paris). */
export const formatCgDate = (iso) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return String(iso);
    const parts = parisDateFormatter.formatToParts(date);
    const get = (type) => parts.find((part) => part.type === type)?.value || '';
    return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}`;
};

/** Valide un montant saisi (≥ 0, 2 décimales max). Retourne { value, error }. */
export const parseCgAmount = (raw) => {
    const text = String(raw ?? '').trim().replace(',', '.');
    if (text === '') return { value: null, error: 'Le montant est obligatoire.' };
    if (!/^\d+(\.\d{1,2})?$/.test(text)) {
        return { value: null, error: 'Montant invalide : nombre positif, 2 décimales maximum.' };
    }
    return { value: Number(text), error: null };
};

/** Normalise une erreur Axios au format `{ error: { code, message, details } }`. */
export const parseCgError = (error) => {
    const status = error?.response?.status ?? null;
    const payload = error?.response?.data;
    const apiError = payload?.error || {};
    let message = apiError.message || null;
    if (!message && typeof payload?.detail === 'string') message = payload.detail;
    return {
        status,
        code: apiError.code || null,
        message,
        details: apiError.details || null,
    };
};

/** 422 VALIDATION_ERROR : `details = { champ: [messages] }` → `{ champ: "messages" }`. */
export const getCgFieldErrors = (parsed) => {
    if (parsed?.status !== 422 || !parsed.details || typeof parsed.details !== 'object') return {};
    return Object.entries(parsed.details).reduce((acc, [field, messages]) => {
        acc[field] = Array.isArray(messages) ? messages.join(' ') : String(messages);
        return acc;
    }, {});
};

export const getCgErrorToast = (parsed) => {
    if (parsed?.code === 'RIB_LINK_ALREADY_SENT') {
        return 'Le lien RIB a déjà été envoyé pour ce geste commercial.';
    }
    if (parsed?.code === 'COMMERCIAL_GESTURE_NOT_SENDABLE') {
        return 'Lien RIB impossible : geste payé ou annulé';
    }
    if (parsed?.code === 'COMMERCIAL_GESTURE_ALREADY_EXISTS') {
        return 'Cette police a déjà un geste commercial';
    }
    if (parsed?.status === 409) {
        return 'Cette police a déjà un geste commercial';
    }
    if (parsed?.code === 'RIB_LINK_GENERATION_FAILED') {
        const extra = parsed.details && typeof parsed.details === 'object'
            ? Object.values(parsed.details).flat().filter(Boolean)[0]
            : null;
        return extra
            ? `Le lien RIB n'a pas pu être généré (${extra})`
            : "Le lien RIB n'a pas pu être généré";
    }
    if (parsed?.code === 'EMAIL_SEND_FAILED' || parsed?.status === 502) {
        return "L'e-mail n'a pas pu être envoyé, réessayez";
    }
    if (parsed?.status === 422 && parsed?.details?.mail) {
        return "L'adresse e-mail du client est absente.";
    }
    switch (parsed?.status) {
        case 403:
            return 'Action non autorisée';
        case 404:
            return parsed.code === 'POLICY_NOT_FOUND'
                ? 'Police introuvable.'
                : 'Geste commercial introuvable.';
        case 422:
            return 'Certains champs sont invalides.';
        default:
            return 'Une erreur est survenue. Veuillez réessayer.';
    }
};

export const unwrapCgData = (response) => {
    if (!response) return null;
    if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        return response.data;
    }
    if (response.id || response.policy_id) return response;
    return null;
};

export const unwrapCgList = (response) => {
    if (Array.isArray(response)) return { items: response, meta: {} };
    if (Array.isArray(response?.data)) return { items: response.data, meta: response.meta || {} };
    return { items: [], meta: response?.meta || {} };
};

/** Aperçu lecture seule d’une police (jamais envoyé au POST / PUT). */
export const mapTripsPolicyPreview = (item) => {
    if (!item) return null;
    const info = item.policy_info || item.policies_info || item;
    const client = info.client_info || {};
    const activity = info.activity_info || {};
    const policyId = info.policy_id || item.policy_id;
    if (!policyId) return null;
    return {
        policy_id: policyId,
        policy_code: info.policy_code || item.policy_code || '',
        reservation_number: activity.reservation_number || item.reservation_number || '',
        first_name: client.first_name || item.first_name || '',
        last_name: client.last_name || item.last_name || '',
        mail: client.email || client.mail || item.mail || '',
    };
};
